import {
  flattenQueryMatchValues,
  prepareQueryMatchState,
} from "../js/query/match.js";

const EMPTY_SEARCH_CATALOG_ARRAY = Object.freeze([]);

const SEARCH_CATALOG_SPECS = Object.freeze({
  fish: Object.freeze({
    keys: Object.freeze([
      "fishId",
      "itemId",
      "encyclopediaId",
      "encyclopediaIconImage",
      "iconItemId",
      "iconImage",
      "name",
      "grade",
      "isPrize",
      "isDried",
      "derivedFromItemId",
    ]),
  }),
  items: Object.freeze({
    keys: Object.freeze([
      "itemId",
      "name",
      "labels",
      "grade",
      "isPrize",
      "isFish",
      "iconItemId",
      "iconImage",
      "sourceKinds",
    ]),
    nestedArrays: Object.freeze({
      labels: projectStringList,
      sourceKinds: projectStringList,
    }),
  }),
  patches: Object.freeze({
    keys: Object.freeze(["patchId", "patchName", "startTsUtc"]),
  }),
  semanticTerms: Object.freeze({
    keys: Object.freeze([
      "layerId",
      "layerName",
      "fieldId",
      "label",
      "description",
      "searchText",
    ]),
  }),
});

function createProjectionCaches() {
  return Object.fromEntries(
    Object.keys(SEARCH_CATALOG_SPECS).map((key) => [key, new WeakMap()]),
  );
}

const SHARED_SEARCH_CATALOG_PROJECTION_CACHES = createProjectionCaches();

function objectLike(value) {
  return (typeof value === "object" && value !== null) || typeof value === "function";
}

function jsonScalar(value) {
  return value === null ||
    typeof value === "string" ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value));
}

function ownContractValue(source, key, path) {
  if (!objectLike(source)) {
    return { present: false, value: undefined };
  }
  const descriptor = Object.getOwnPropertyDescriptor(source, key);
  if (!descriptor) {
    return { present: false, value: undefined };
  }
  if (!("value" in descriptor)) {
    throw new TypeError(`${path} must be a data property`);
  }
  const stored = descriptor.value;
  return {
    present: true,
    // Datastar stores each reactive property as a signal accessor on the Proxy
    // target. Invoking that own data value reads the signal without entering the
    // Proxy get trap, whose missing/null behavior mutates the signal graph.
    value: typeof stored === "function" ? stored() : stored,
  };
}

function ownArrayLength(source, path) {
  const length = ownContractValue(source, "length", `${path}.length`);
  if (!length.present || !Number.isSafeInteger(length.value) || length.value < 0) {
    throw new TypeError(`${path} must have an array length`);
  }
  return length.value;
}

function assertContractArray(value, path) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${path} must be an array`);
  }
  return value;
}

function projectScalarArray(source, path) {
  assertContractArray(source, path);
  const projected = [];
  const length = ownArrayLength(source, path);
  for (let index = 0; index < length; index += 1) {
    const item = ownContractValue(source, String(index), `${path}[${index}]`);
    if (!item.present) {
      throw new TypeError(`${path} must not be sparse`);
    }
    if (!jsonScalar(item.value)) {
      throw new TypeError(`${path}[${index}] must be a JSON scalar`);
    }
    projected.push(item.value);
  }
  return Object.freeze(projected);
}

function projectRecord(source, keys, path, nestedArrays = {}) {
  if (!objectLike(source) || Array.isArray(source)) {
    throw new TypeError(`${path} must be an object`);
  }
  const projected = {};
  for (const key of keys) {
    const field = ownContractValue(source, key, `${path}.${key}`);
    if (!field.present) {
      continue;
    }
    const nestedProjector = nestedArrays[key];
    if (nestedProjector) {
      projected[key] = nestedProjector(field.value, `${path}.${key}`);
      continue;
    }
    if (!jsonScalar(field.value)) {
      throw new TypeError(`${path}.${key} must be a JSON scalar`);
    }
    projected[key] = field.value;
  }
  return Object.freeze(projected);
}

function projectRecordArray(source, keys, path, nestedArrays = {}) {
  assertContractArray(source, path);
  const length = ownArrayLength(source, path);
  const projected = [];
  for (let index = 0; index < length; index += 1) {
    const row = ownContractValue(source, String(index), `${path}[${index}]`);
    if (!row.present) {
      throw new TypeError(`${path} must not be sparse`);
    }
    projected.push(projectRecord(row.value, keys, `${path}[${index}]`, nestedArrays));
  }
  return Object.freeze(projected);
}

function projectStringList(source, path) {
  const values = projectScalarArray(source, path);
  if (values.some((value) => typeof value !== "string")) {
    throw new TypeError(`${path} must contain only strings`);
  }
  return values;
}

function cachedProjection(cache, source, revision, project) {
  if (source === undefined) {
    return EMPTY_SEARCH_CATALOG_ARRAY;
  }
  assertContractArray(source, "catalog");
  const cached = cache.get(source);
  if (cached && Object.is(cached.revision, revision)) {
    return cached.value;
  }
  const value = project(source);
  cache.set(source, { revision, value });
  return value;
}

function catalogArraySource(catalog, key) {
  const field = ownContractValue(catalog, key, `catalog.${key}`);
  return field.present ? field.value : undefined;
}

/**
 * Projects the reactive runtime catalog onto the one plain shape consumed by
 * search. The source arrays remain authoritative; `revision` only invalidates
 * a same-identity source after an in-place update.
 */
function projectSearchCatalogWithCaches(catalog, revision, caches) {
  const source = objectLike(catalog) && !Array.isArray(catalog) ? catalog : {};
  const projected = {};
  for (const [key, spec] of Object.entries(SEARCH_CATALOG_SPECS)) {
    projected[key] = cachedProjection(
      caches[key],
      catalogArraySource(source, key),
      revision,
      (rows) => projectRecordArray(
        rows,
        spec.keys,
        `catalog.${key}`,
        spec.nestedArrays,
      ),
    );
  }
  return Object.freeze(projected);
}

export function projectSearchCatalog(catalog, { revision } = {}) {
  return projectSearchCatalogWithCaches(
    catalog,
    revision,
    SHARED_SEARCH_CATALOG_PROJECTION_CACHES,
  );
}

/**
 * Owns revision-stable search catalog identity for one live consumer. A
 * revision is meaningful only inside that consumer's mounted runtime; keeping
 * this closure component-local prevents equal revisions from unrelated map
 * instances sharing a projection.
 */
export function createSearchCatalogProjector() {
  const projectionCaches = createProjectionCaches();
  let hasRevision = false;
  let revision;
  let projection;

  return (catalog, options = {}) => {
    const nextRevision = options?.revision;
    const hasNextRevision = nextRevision !== undefined;
    if (
      hasNextRevision &&
      (!Number.isSafeInteger(nextRevision) || nextRevision < 0)
    ) {
      throw new TypeError("search catalog revision must be a non-negative safe integer");
    }
    if (hasNextRevision && hasRevision && nextRevision < revision) {
      throw new TypeError("search catalog revision must not regress");
    }
    if (hasNextRevision && hasRevision && Object.is(nextRevision, revision)) {
      return projection;
    }

    const nextProjection = projectSearchCatalogWithCaches(
      catalog,
      nextRevision,
      projectionCaches,
    );
    hasRevision = hasNextRevision;
    revision = nextRevision;
    projection = hasNextRevision ? nextProjection : undefined;
    return nextProjection;
  };
}

function preparedMatchValues(values) {
  return flattenQueryMatchValues(values)
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .map((value) => prepareQueryMatchState(value));
}

function projectedItemLabels(item) {
  const labels = [];
  for (const value of [item?.name, ...(Array.isArray(item?.labels) ? item.labels : [])]) {
    const label = String(value ?? "").trim();
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  }
  return labels;
}

function prepareProjectedCatalog(catalog) {
  const fish = Array.isArray(catalog?.fish) ? catalog.fish : [];
  const items = Array.isArray(catalog?.items) ? catalog.items : [];
  const semanticTerms = Array.isArray(catalog?.semanticTerms) ? catalog.semanticTerms : [];
  return {
    fishSearch: fish.map((entry) => preparedMatchValues([
      entry?.fishId,
      entry?.itemId,
      entry?.name,
    ])),
    fishExact: fish.map((entry) => preparedMatchValues([
      entry?.name,
      entry?.label,
      entry?.slug,
      ...(Array.isArray(entry?.labels) ? entry.labels : []),
    ])),
    itemSearch: items.map((entry) => ({
      itemId: entry?.itemId,
      values: preparedMatchValues([entry?.itemId, entry?.name, projectedItemLabels(entry)]),
    })),
    itemExact: items.map((entry) => preparedMatchValues([
      entry?.name,
      entry?.label,
      entry?.slug,
      ...(Array.isArray(entry?.labels) ? entry.labels : []),
    ])),
    semanticSearch: semanticTerms.map((entry) => preparedMatchValues([
      entry?.fieldId,
      entry?.label,
      entry?.description,
      entry?.layerName,
      entry?.searchText,
    ])),
    semanticExact: semanticTerms.map((entry) => preparedMatchValues([
      entry?.label,
      entry?.name,
      entry?.fieldName,
      entry?.fieldLabel,
      entry?.slug,
      entry?.description,
      entry?.searchText,
    ])),
  };
}

const IS_SEARCH_CATALOG_WORKER =
  typeof globalThis.WorkerGlobalScope === "function" &&
  globalThis instanceof globalThis.WorkerGlobalScope;

if (IS_SEARCH_CATALOG_WORKER) {
  globalThis.addEventListener("message", (event) => {
    const requestId = Number.parseInt(event?.data?.requestId, 10);
    if (!Number.isSafeInteger(requestId) || requestId < 1) {
      return;
    }
    try {
      globalThis.postMessage({
        requestId,
        prepared: prepareProjectedCatalog(event?.data?.catalog),
      });
    } catch (error) {
      globalThis.postMessage({
        requestId,
        error: String(error?.message || error || "catalog preparation failed"),
      });
    }
  });
}

function projectedCatalogArrays(stateBundle) {
  const catalog = stateBundle?.state?.catalog || {};
  return [
    catalogArrayOrEmpty(catalog.fish),
    catalogArrayOrEmpty(catalog.items),
    catalogArrayOrEmpty(catalog.patches),
    catalogArrayOrEmpty(catalog.semanticTerms),
    catalogArrayOrEmpty(catalog.zones),
    catalogArrayOrEmpty(catalog.tradeNpcs),
  ];
}

function preparedCatalogArrays(stateBundle) {
  const catalog = stateBundle?.state?.catalog || {};
  return [
    catalogArrayOrEmpty(catalog.fish),
    catalogArrayOrEmpty(catalog.items),
    catalogArrayOrEmpty(catalog.semanticTerms),
  ];
}

function catalogArrayOrEmpty(value) {
  return Array.isArray(value) && value.length > 0
    ? value
    : EMPTY_SEARCH_CATALOG_ARRAY;
}

function sameCatalogArrays(left, right) {
  return Boolean(left && right) &&
    left.length === right.length &&
    left.every((value, index) => value === right[index]);
}

function createSearchCatalogWorker() {
  if (
    typeof globalThis.Blob !== "function" ||
    typeof globalThis.Worker !== "function" ||
    typeof globalThis.URL?.createObjectURL !== "function"
  ) {
    throw new Error("search catalog worker APIs are unavailable");
  }
  const moduleUrl = new URL("./map-search-catalog-projection.js", import.meta.url).href;
  // The map page is cross-origin isolated. Loading the asset URL directly as a
  // worker document would require that individual response to repeat COEP;
  // this module bootstrap inherits the document policy and imports the same
  // canonical implementation as a normal same-origin module dependency.
  const bootstrapUrl = URL.createObjectURL(new Blob(
    [`import ${JSON.stringify(moduleUrl)};`],
    { type: "text/javascript" },
  ));
  try {
    const createdWorker = new Worker(bootstrapUrl, { type: "module" });
    // Worker startup is asynchronous: a browser may still need this URL after
    // the constructor returns. The preparation owner releases it at teardown.
    return { worker: createdWorker, dispose: () => URL.revokeObjectURL(bootstrapUrl) };
  } catch (error) {
    URL.revokeObjectURL(bootstrapUrl);
    throw error;
  }
}

export function createSearchCatalogPreparationOwner(options = {}) {
  const workerFactory = options.workerFactory || createSearchCatalogWorker;
  let worker = null;
  let workerListeners = null;
  let disposeWorkerBootstrap = null;
  let requestId = 0;
  let currentArrays = null;
  let preparedArrays = null;
  let pendingArrays = null;
  let pendingBundle = null;
  let state = "idle";
  let failure = null;

  const terminateWorker = () => {
    if (worker && workerListeners) {
      worker.removeEventListener?.("message", workerListeners.message);
      worker.removeEventListener?.("error", workerListeners.error);
      worker.removeEventListener?.("messageerror", workerListeners.messageerror);
    }
    worker?.terminate?.();
    disposeWorkerBootstrap?.();
    disposeWorkerBootstrap = null;
    worker = null;
    workerListeners = null;
  };
  const fail = (error, failedWorker = worker, failedRequestId = requestId) => {
    if (failedWorker !== worker || failedRequestId !== requestId) {
      return;
    }
    failure = error instanceof Error ? error : new Error(String(error));
    state = "failed";
    pendingArrays = null;
    pendingBundle = null;
    terminateWorker();
    options.onFailed?.(failure);
  };

  const finish = (
    stateBundle,
    nextPreparedArrays = preparedArrays,
    preparedPayload,
    shouldPrime = false,
  ) => {
    let rollbackPreparedValues = null;
    try {
      if (shouldPrime) {
        const rollback = options.primePreparedValues?.(stateBundle, preparedPayload);
        rollbackPreparedValues = typeof rollback === "function" ? rollback : null;
      }
      const preparation = options.finishPreparation?.(stateBundle);
      state = "publishing";
      options.onPrepared?.(preparation);
      preparedArrays = nextPreparedArrays;
      pendingArrays = null;
      state = "ready";
      failure = null;
      pendingBundle = null;
      terminateWorker();
      return true;
    } catch (error) {
      rollbackPreparedValues?.();
      fail(error);
      return false;
    }
  };

  return {
    get pending() {
      return state === "pending" || state === "publishing";
    },
    get ready() {
      return state === "ready";
    },
    get failed() {
      return state === "failed";
    },
    get failure() {
      return failure;
    },
    prepare(stateBundle) {
      const arrays = projectedCatalogArrays(stateBundle);
      const nextPreparedArrays = preparedCatalogArrays(stateBundle);
      if (sameCatalogArrays(currentArrays, arrays)) {
        return state === "ready";
      }
      if (state === "publishing") {
        throw new Error("search catalog publication must not be reentrant");
      }
      currentArrays = arrays;
      failure = null;

      if (state === "pending" && sameCatalogArrays(pendingArrays, nextPreparedArrays)) {
        // Patch and zone projections are cheap and remain synchronous. Keep the
        // in-flight fish/item/semantic generation, but finish it against the
        // latest complete bundle when its one atomic result arrives.
        pendingBundle = stateBundle;
        return false;
      }

      requestId += 1;
      terminateWorker();

      if (
        sameCatalogArrays(preparedArrays, nextPreparedArrays) ||
        (nextPreparedArrays[0].length === 0 &&
          nextPreparedArrays[1].length === 0 &&
          nextPreparedArrays[2].length === 0)
      ) {
        return finish(stateBundle, nextPreparedArrays);
      }

      state = "pending";
      pendingArrays = nextPreparedArrays;
      pendingBundle = stateBundle;
      try {
        const created = workerFactory();
        worker = created.worker;
        disposeWorkerBootstrap = created.dispose;
        const activeWorker = worker;
        const activeRequestId = requestId;
        const accept = (event) => {
          const message = event?.data || {};
          if (
            activeWorker !== worker ||
            activeRequestId !== requestId ||
            message.requestId !== activeRequestId ||
            state !== "pending" ||
            !pendingBundle
          ) {
            return;
          }
          if (message.error) {
            fail(new Error(message.error), activeWorker, activeRequestId);
            return;
          }
          const bundle = pendingBundle;
          const completedArrays = pendingArrays;
          try {
            finish(bundle, completedArrays, message.prepared, true);
          } catch (error) {
            fail(error, activeWorker, activeRequestId);
          }
        };
        const reject = (event) => {
          fail(
            new Error(event?.message || "search catalog worker failed"),
            activeWorker,
            activeRequestId,
          );
        };
        const rejectMessage = (event) => {
          fail(
            new Error(event?.message || "search catalog worker response could not be cloned"),
            activeWorker,
            activeRequestId,
          );
        };
        workerListeners = { message: accept, error: reject, messageerror: rejectMessage };
        worker.addEventListener("message", accept);
        worker.addEventListener("error", reject);
        worker.addEventListener("messageerror", rejectMessage);
        worker.postMessage({
          requestId,
          catalog: {
            fish: nextPreparedArrays[0],
            items: nextPreparedArrays[1],
            semanticTerms: nextPreparedArrays[2],
          },
        });
      } catch (error) {
        fail(error);
      }
      return false;
    },
    dispose() {
      requestId += 1;
      state = "idle";
      failure = null;
      currentArrays = null;
      preparedArrays = null;
      pendingArrays = null;
      pendingBundle = null;
      terminateWorker();
    },
  };
}
